/**
 * Putting songs into a player's queue, the three ways (docs/RADIO-REVIEW.md 4.1):
 *
 * - **keep** -- where it was put (next, or where it was dropped); nothing moves.
 * - **rearrange** -- where it was put, and the songs after it reordered to follow
 *   it well. Pinned songs and clips (anything with a reason: a rule's ID, an
 *   advert, a marker's content) stay where they are.
 * - **best** -- in the gap where it fits best between its neighbours; nothing
 *   else moves.
 *
 * The engine decides (`POST /api/arrange/insert`, nova/arrange); the queue is
 * changed here, in one step, by item key, so a song that ends while the engine
 * answers changes nothing that matters.
 *
 * `chooseAndInsert` asks which, unless the given preference holds a default.
 */

import { engineBase } from "../api";
import { askInsert } from "../shell/InsertDialog";
import { toast } from "../shell/Toast";
import type { Player, QueueTrack } from "./player";
import { readPref, writePref } from "./prefs";

export type InsertMode = "keep" | "rearrange" | "best";
export type InsertDefault = InsertMode | "ask";

export const INSERT_CHOICES: { id: InsertMode; label: string; here: string; hint: string }[] = [
  { id: "keep", label: "Play next, keep the rest", here: "Put it here, keep the rest", hint: "Nothing else moves." },
  { id: "rearrange", label: "Play next and rearrange what follows", here: "Put it here and rearrange what follows", hint: "The songs after it are reordered to suit it. Pinned songs and clips stay where they are." },
  { id: "best", label: "Put it where it fits best", here: "Put it where it fits best", hint: "Between the songs it suits most, by tempo, energy, key and groove. Nothing else moves." },
];

interface Answer {
  order?: number[];
  at?: number;
  moved?: number;
  error?: string;
}

async function ask(body: unknown): Promise<Answer> {
  try {
    const res = await fetch(`${engineBase()}/api/arrange/insert`, { method: "POST", body: JSON.stringify(body) });
    return (await res.json()) as Answer;
  } catch {
    return { error: "the engine is not running" };
  }
}

/**
 * Put `tracks` into `p`'s queue at `at` (1 = right after the item playing) the
 * way `mode` says. Several songs go in together; "rearrange" suits what follows
 * to the last of them, "best" places each in turn. Returns where the first one
 * went (1-based from the item playing) and how many songs moved, or why not.
 * `reason` marks what goes in as a clip (a jingle put in by hand), which a later
 * rearrange leaves in place.
 */
export async function insertInto(p: Player, tracks: QueueTrack[], mode: InsertMode, at = 1, reason?: string): Promise<{ at: number; moved: number } | string> {
  const allowed = tracks.filter((t) => !p.isUnplayable(t.id));
  if (!allowed.length) return tracks.length === 1 ? `“${tracks[0].title}” cannot play until it is marked fixed.` : "None of these songs can play until they are marked fixed.";
  const s = p.get();
  if (!s.current || s.index < 0) {
    p.arrangeUpcoming([...s.queue.slice(Math.max(0, s.index + 1)).map((x) => x.key), ...allowed.map((track) => ({ track, reason }))]);
    return { at: 1, moved: 0 };
  }
  const upcoming = s.queue.slice(s.index);
  const place = Math.max(1, Math.min(at, upcoming.length));
  const keys = upcoming.map((x) => x.key);
  const queueBody = upcoming.map((x) => ({ trackId: x.track.id, pinned: !!x.pinned, clip: !!x.reason }));
  const fresh = (track: QueueTrack) => ({ track, reason });

  if (mode === "keep") {
    p.arrangeUpcoming([...keys.slice(1, place), ...allowed.map(fresh), ...keys.slice(place)]);
    return { at: place, moved: 0 };
  }
  if (mode === "best") {
    // Each song in turn, into the queue as it stands with the ones before it placed.
    let first = 0;
    for (const track of allowed) {
      const now = p.get();
      const list = now.queue.slice(Math.max(0, now.index));
      const out = await ask({ mode, insert: track.id, at: 1, queue: list.map((x) => ({ trackId: x.track.id, pinned: !!x.pinned, clip: !!x.reason })) });
      if (out.error || !out.order) return `Could not place “${track.title}”: ${out.error ?? "no answer"}.`;
      const ks = list.map((x) => x.key);
      p.arrangeUpcoming(out.order.slice(1).map((k) => (k === -1 ? fresh(track) : ks[k])));
      first ||= out.at ?? 1;
    }
    return { at: first, moved: 0 };
  }
  // Rearrange: all of them where they were put; what follows suits the last.
  const last = allowed[allowed.length - 1];
  const before = allowed.slice(0, -1);
  const out = await ask({ mode, insert: last.id, at: place + before.length, queue: [
    ...queueBody.slice(0, place), ...before.map((t) => ({ trackId: t.id, pinned: true })), ...queueBody.slice(place),
  ] });
  if (out.error || !out.order) return `Could not rearrange: ${out.error ?? "no answer"}.`;
  const named = [...keys.slice(0, place), ...before.map(fresh), ...keys.slice(place)];
  p.arrangeUpcoming(out.order.slice(1).map((k) => (k === -1 ? fresh(last) : named[k])));
  return { at: place, moved: out.moved ?? 0 };
}

/**
 * Ask which way (unless `prefKey` holds a default), then insert; only a refusal
 * is said. `at` is where the songs were dropped (1 = next); `dropped` says the
 * choices should read "put it here" rather than "play next"; `reason` marks
 * clips, which go in where they were put without asking.
 */
/**
 * Which way songs go in: the default `prefKey` holds, or the person's answer
 * (null when they cancel). A clip (a jingle, an ID) just goes where it was put:
 * arranging is about songs. Apart from inserting so another window can ask here
 * and insert there.
 */
/**
 * Where the default way in is kept: a preference key (this window's own), or a store
 * a plugin keeps elsewhere (Radio keeps On air's in the engine, shared by every window).
 */
export interface InsertDefaultStore {
  get: () => InsertDefault;
  set: (mode: InsertMode) => void;
}
export type InsertDefaultFrom = string | InsertDefaultStore;

const storeOf = (from: InsertDefaultFrom): InsertDefaultStore => typeof from === "string"
  ? { get: () => readPref<InsertDefault>(from, "ask"), set: (mode) => writePref(from, mode) }
  : from;

export async function chooseMode(tracks: QueueTrack[], { prefKey, dropped = false, reason }: { prefKey: InsertDefaultFrom; dropped?: boolean; reason?: string }): Promise<InsertMode | null> {
  const store = storeOf(prefKey);
  const preset = reason ? "keep" : store.get();
  return preset === "ask" ? askInsert({ tracks, remember: store.set, dropped }) : preset;
}

/** Ask how to insert (unless a default is set) and insert. True when the songs went in. */
export async function chooseAndInsert(p: Player, tracks: QueueTrack[], { at = 1, prefKey, dropped = false, reason }: { at?: number; prefKey: InsertDefaultFrom; dropped?: boolean; reason?: string }): Promise<boolean> {
  if (!tracks.length) return false;
  // Remember the item it goes after, so a song ending while the person chooses
  // does not shift the place by one.
  const before = p.get();
  const anchor = before.index >= 0 ? before.queue[before.index + at - 1]?.key : undefined;
  const mode = await chooseMode(tracks, { prefKey, dropped, reason });
  if (!mode) return false;
  const now = p.get();
  const found = anchor ? now.queue.findIndex((x) => x.key === anchor) : -1;
  const place = found < 0 ? at : Math.max(1, found - now.index + 1);
  // Only a refusal is said: the song appearing in the queue is the answer, and a
  // note for every add piles up over the desk.
  const done = await insertInto(p, tracks, mode, place, reason);
  if (typeof done === "string") toast(done, "bad");
  return typeof done !== "string";
}
